import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { Gender } from "@/Common/Models/Patient/PatientEnums";

interface IPharmaNetCredentialsProps {
    onClose: () => void;
    patient?: MyleModelsPatientPatient;
    context: PharmaNetContext
}

class PharmaNetCredentialsState {
    public username: string;
    public password: string;
    public shouldRenderLoginPopup: boolean;

    public constructor(init?: Partial<PharmaNetCredentialsState>) {
        Object.assign(this, init);
    }

    public static getDefault(): PharmaNetCredentialsState {
        return new PharmaNetCredentialsState({
            username: "",
            password: "",
            shouldRenderLoginPopup: false
        });
    }
}

enum PharmaNetContext {
    Patient,
    Settings
}

class PharmaNetCredentials extends BaseReactComponent<IPharmaNetCredentialsProps, PharmaNetCredentialsState> {
    constructor(props: IPharmaNetCredentialsProps) {
        super(props);
        this.state = PharmaNetCredentialsState.getDefault();
    }

    public componentDidMount(): void {
        this.setOverlay(
            "PharmaNetCredentialsOverlay",
            {
                transparent: false,
                onClicked: () => this.props.onClose()
            }
        );

        this.getCredentials();
    }

    public render(): JSX.Element {
        if (!this.state.shouldRenderLoginPopup)
            return null;

        return (
            <Popup title={cacheStore.GetString("PharmaNetLoginLabel")} onCloseDelegate={() => this.props.onClose()}>
                <div className="p-body">
                    <div className="flex-container" style={{ margin: "10px" }}>
                        <label style={{ paddingTop: "5px" }}>{cacheStore.GetString("UserNameLabel")}</label>
                        <TextboxControl
                            placeholder={cacheStore.GetString("UserNameLabel")}
                            onChange={text => this.mutateState(s => s.username = text)}
                            value={this.state.username}
                            style={{ marginLeft: "10px" }}
                        />
                    </div>
                    <div className="flex-container" style={{ margin: "10px" }}>
                        <label style={{ paddingTop: "5px" }} htmlFor="pwd">{cacheStore.GetString("PasswordLabel")}</label>
                        <TextboxControl
                            placeholder={cacheStore.GetString("PasswordLabel")}
                            type="password"
                            onChange={text => this.mutateState(s => s.password = text)}
                            value={this.state.password}
                            style={{ marginLeft: "10px" }}
                            onKeyDown={e => this.handleKeyPress(e)}
                        />
                    </div>
                </div>
                <div className="p-footer">
                    <div className="text-right">
                        <button
                            className="btn btn-default push-right-5"
                            onClick={() => this.props.onClose()}
                        >
                            {cacheStore.GetString("CancelLabel")}
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={IsNullOrWhiteSpace(this.state.username) || IsNullOrWhiteSpace(this.state.password)}
                            onClick={() => this.saveCredentials()}
                        >
                            {cacheStore.GetString(this.props.context === PharmaNetContext.Settings ? "SaveLabel" : "LoginLabel")}
                        </button>
                    </div>
                </div>
            </Popup>
        );
    }

    private openPharmaNetWindow(): void {
        Client.openWindowWithPostData(
            "https://swan.medinet.ca/cgi-bin/myle.cgi",
            this.getPharmaNetPostData(),
            "toolbar=no,menubar=no,location=no,directories=no,resizable=yes,titlebar=no,scrollbars=yes,status=yes"
        );
    }

    private getCredentials(): void {
        if (!sessionStore.hasPharmaNetCredentials()) {
            this.mutateState(s => s.shouldRenderLoginPopup = true);
            return;
        }

        this.mutateState(
            s => {
                s.username = sessionStore.pharmaNetUsername;
                s.password = sessionStore.pharmaNetPassword;
            },
            () => {
                switch (this.props.context) {
                    case PharmaNetContext.Settings:
                        this.mutateState(s => s.shouldRenderLoginPopup = true);
                        break;
                    case PharmaNetContext.Patient:
                        this.openPharmaNetWindow();
                        this.props.onClose();
                        break;
                }
            }
        );
    }

    private saveCredentials(): void {
        sessionStore.savePharmaNetCredentials(this.state.username, this.state.password);

        if (this.props.context === PharmaNetContext.Patient)
            this.openPharmaNetWindow();

        this.props.onClose();
    }

    private getPharmaNetPostData(): Record<string, string> {
        const data: Record<string, string> = {};

        // credentials
        data.login = this.state.username;
        data.passwd = this.state.password;

        // preselect patient if possible
        let healthCard = this.props.patient?.HealthCards?.firstOrDefault();
        if (healthCard?.ProvinceLookupId === cacheStore.FindLookupByLookupCode("CanadaState_BC").LookupID)
            data.phn = healthCard.Number.trim();
        else {
            // note: must provide *all* fields or none
            let firstName = this.props.patient?.IndividualResponse?.FirstName;
            let lastName = this.props.patient?.IndividualResponse?.LastName;
            let dateOfBirth = moment(this.props.patient?.IndividualResponse?.DateOfBirth);
            let gender = this.getAbbreviatedGender();

            if (IsNullOrWhiteSpace(firstName) || IsNullOrWhiteSpace(lastName) || dateOfBirth.isValid() || IsNullOrWhiteSpace(gender)) {
                data.first = firstName;
                data.last = lastName;
                data.gender = gender;
                data.date = dateOfBirth.format("YYYY");
            }
        }

        return data;
    }

    private getAbbreviatedGender(): string {
        switch (this.props.patient?.Gender()) {
            case Gender.Male:
                return "M";
            case Gender.Female:
                return "F";
            case Gender.Unspecified:
                return "";
        }
    }

    private handleKeyPress(e: React.KeyboardEvent): void {
        if (e.keyCode === 13) {
            this.saveCredentials();
        }
    }
}

export { PharmaNetContext, PharmaNetCredentials };
